// src/scripts/api.js
import axios from "axios";

/**
 * CRA/CRACO (webpack) does NOT support `import.meta` safely.
 * Keep this dead simple:
 * - default to same-origin `/api` (nginx proxies it to :3002)
 * - allow optional runtime override via window.__API_BASE__
 * - allow CRA env override via REACT_APP_API_BASE
 */

const API_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  process.env.REACT_APP_API_BASE ||
  "/api";

function isXdbOptional(urlPath) {
  const u = String(urlPath || "");

  // Always optional for these “global” endpoints
  return (
    u.startsWith("/auth/") ||
    u.startsWith("/db/") ||
    u.startsWith("/health") ||
    u === "/health" ||
    u.startsWith("/public/") ||
    u.startsWith("/message/")
  );
}
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// Attach auth + active DB on every request
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("auth_token")?.trim() || "";
    const activeDb = sessionStorage.getItem("active_db")?.trim() || "";

    // axios may pass headers as undefined / AxiosHeaders
    config.headers = config.headers || {};

    // Normalize URL path relative to baseURL
    // (config.url might be "customers", "/customers", or full URL)
    let urlPath = String(config.url || "");
    // If it's a full URL, strip origin + base (best effort)
    try {
      if (/^https?:\/\//i.test(urlPath)) {
        const u = new URL(urlPath);
        urlPath = u.pathname + (u.search || "");
      }
    } catch (_) {
      // ignore URL parse errors
    }
    // If caller mistakenly included "/api/..." while baseURL is "/api", normalize it
    while (urlPath.startsWith("/api/")) urlPath = urlPath.slice(4);
    urlPath = "/" + urlPath.replace(/^\/+/, "");

    // Auth header
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // X-DB header: required for most routes, optional for auth/db/health/public
    const optional = isXdbOptional(urlPath);

    if (!optional) {
      if (!activeDb) {
        // hard fail locally rather than hammering backend
        return Promise.reject(
          new Error("Missing X-DB header (active database not selected)")
        );
      }
      config.headers["X-DB"] = activeDb;
    } else {
      // harmless if present; helps if backend wants context anyway
      if (activeDb) config.headers["X-DB"] = activeDb;
    }

    return config;
  },
  (err) => Promise.reject(err)
);

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const cfg = err?.config || {};
    const fullUrl =
      cfg?.baseURL && cfg?.url ? String(cfg.baseURL) + String(cfg.url) : cfg?.url;

    console.error("[API ERROR]", {
      baseURL: API_BASE,
      url: fullUrl,
      method: cfg?.method,
      status: err?.response?.status,
      data: err?.response?.data,
      message: err?.message,
    });

    return Promise.reject(err);
  }
);
